import { useState, useEffect } from 'react'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Label, Select, Input } from '../components/ui'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'

interface TimeSettings {
  id: string
  team_id: string
  work_week_start: number // 0-6 (Sunday-Saturday)
  work_week_hours: number
  overtime_threshold: number
  overtime_multiplier: number
  double_time_threshold: number
  double_time_multiplier: number
  meal_break_duration: number // minutes
  meal_break_after: number // hours
  rest_break_duration: number // minutes
  rest_break_after: number // hours
  auto_clock_out_after: number // hours
  require_photo_verification: boolean
  require_location_verification: boolean
  allow_early_clock_in: number // minutes
  allow_late_clock_out: number // minutes
  round_clock_times: boolean
  round_interval: number // minutes
  created_at: number
  updated_at: number
}

export default function TimeSettings() {
  const { currentCompany } = useCompany()
  const { hasPermission } = useAuth()
  const [settings, setSettings] = useState<TimeSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [editedSettings, setEditedSettings] = useState<Partial<TimeSettings>>({})
  const [hasChanges, setHasChanges] = useState(false)

  const canEdit = hasPermission('manage_settings')

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const teams = data.teams || []
        
        // Find a team where the user is a member
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch settings
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchSettings()
  }, [currentTeamId])

  const fetchSettings = async () => {
    if (!currentTeamId) return

    try {
      setLoading(true)
      setError(null)

      // For now, use mock data since the API endpoint doesn't exist yet
      const mockSettings: TimeSettings = {
        id: '1',
        team_id: currentTeamId,
        work_week_start: 1, // Monday
        work_week_hours: 40,
        overtime_threshold: 40,
        overtime_multiplier: 1.5,
        double_time_threshold: 60,
        double_time_multiplier: 2.0,
        meal_break_duration: 30,
        meal_break_after: 5,
        rest_break_duration: 10,
        rest_break_after: 4,
        auto_clock_out_after: 12,
        require_photo_verification: false,
        require_location_verification: true,
        allow_early_clock_in: 5,
        allow_late_clock_out: 5,
        round_clock_times: true,
        round_interval: 15,
        created_at: Date.now(),
        updated_at: Date.now()
      }

      setSettings(mockSettings)
      setEditedSettings(mockSettings)
    } catch (err) {
      console.error('Failed to fetch settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!currentTeamId || !settings) return

    try {
      setSaving(true)
      setError(null)

      // For now, just update local state
      const updatedSettings = { ...settings, ...editedSettings, updated_at: Date.now() }
      setSettings(updatedSettings)
      setHasChanges(false)

      // Show success message
      alert('Settings saved successfully!')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (key: keyof TimeSettings, value: any) => {
    setEditedSettings({ ...editedSettings, [key]: value })
    setHasChanges(true)
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="space-y-4">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error && !currentTeamId) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">No settings found</p>
        </div>
      </div>
    )
  }

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Time & Attendance Settings</h1>
        {canEdit && hasChanges && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Work Week Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Work Week</CardTitle>
          <CardDescription>Configure standard work week settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="work_week_start">Week Starts On</Label>
              <Select
                id="work_week_start"
                value={editedSettings.work_week_start?.toString() || '1'}
                onChange={(e) => updateSetting('work_week_start', parseInt(e.target.value))}
                disabled={!canEdit}
              >
                {weekDays.map((day, index) => (
                  <option key={index} value={index}>{day}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="work_week_hours">Standard Work Week (hours)</Label>
              <Input
                id="work_week_hours"
                type="number"
                value={editedSettings.work_week_hours || 40}
                onChange={(e) => updateSetting('work_week_hours', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="168"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overtime Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Overtime Rules</CardTitle>
          <CardDescription>Configure overtime calculation thresholds and rates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="overtime_threshold">Overtime After (hours/week)</Label>
              <Input
                id="overtime_threshold"
                type="number"
                value={editedSettings.overtime_threshold || 40}
                onChange={(e) => updateSetting('overtime_threshold', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="168"
              />
            </div>
            <div>
              <Label htmlFor="overtime_multiplier">Overtime Rate</Label>
              <Select
                id="overtime_multiplier"
                value={editedSettings.overtime_multiplier?.toString() || '1.5'}
                onChange={(e) => updateSetting('overtime_multiplier', parseFloat(e.target.value))}
                disabled={!canEdit}
              >
                <option value="1.0">1.0x (Regular)</option>
                <option value="1.5">1.5x (Time and a half)</option>
                <option value="2.0">2.0x (Double time)</option>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="double_time_threshold">Double Time After (hours/week)</Label>
              <Input
                id="double_time_threshold"
                type="number"
                value={editedSettings.double_time_threshold || 60}
                onChange={(e) => updateSetting('double_time_threshold', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="168"
              />
            </div>
            <div>
              <Label htmlFor="double_time_multiplier">Double Time Rate</Label>
              <Select
                id="double_time_multiplier"
                value={editedSettings.double_time_multiplier?.toString() || '2.0'}
                onChange={(e) => updateSetting('double_time_multiplier', parseFloat(e.target.value))}
                disabled={!canEdit}
              >
                <option value="1.5">1.5x</option>
                <option value="2.0">2.0x</option>
                <option value="2.5">2.5x</option>
                <option value="3.0">3.0x</option>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Break Rules */}
      <Card>
        <CardHeader>
          <CardTitle>Break Rules</CardTitle>
          <CardDescription>Configure meal and rest break requirements</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="meal_break_duration">Meal Break Duration (minutes)</Label>
              <Input
                id="meal_break_duration"
                type="number"
                value={editedSettings.meal_break_duration || 30}
                onChange={(e) => updateSetting('meal_break_duration', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="120"
              />
            </div>
            <div>
              <Label htmlFor="meal_break_after">Required After (hours)</Label>
              <Input
                id="meal_break_after"
                type="number"
                value={editedSettings.meal_break_after || 5}
                onChange={(e) => updateSetting('meal_break_after', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="12"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="rest_break_duration">Rest Break Duration (minutes)</Label>
              <Input
                id="rest_break_duration"
                type="number"
                value={editedSettings.rest_break_duration || 10}
                onChange={(e) => updateSetting('rest_break_duration', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="60"
              />
            </div>
            <div>
              <Label htmlFor="rest_break_after">Required After (hours)</Label>
              <Input
                id="rest_break_after"
                type="number"
                value={editedSettings.rest_break_after || 4}
                onChange={(e) => updateSetting('rest_break_after', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="12"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clock In/Out Rules */}
      <Card>
        <CardHeader>
          <CardTitle>Clock In/Out Rules</CardTitle>
          <CardDescription>Configure time clock policies and rounding</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="auto_clock_out_after">Auto Clock-Out After (hours)</Label>
            <Input
              id="auto_clock_out_after"
              type="number"
              value={editedSettings.auto_clock_out_after || 12}
              onChange={(e) => updateSetting('auto_clock_out_after', parseInt(e.target.value))}
              disabled={!canEdit}
              min="0"
              max="24"
            />
            <p className="text-sm text-gray-600 mt-1">Automatically clock out employees after this many hours</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="allow_early_clock_in">Allow Early Clock-In (minutes)</Label>
              <Input
                id="allow_early_clock_in"
                type="number"
                value={editedSettings.allow_early_clock_in || 5}
                onChange={(e) => updateSetting('allow_early_clock_in', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="60"
              />
            </div>
            <div>
              <Label htmlFor="allow_late_clock_out">Allow Late Clock-Out (minutes)</Label>
              <Input
                id="allow_late_clock_out"
                type="number"
                value={editedSettings.allow_late_clock_out || 5}
                onChange={(e) => updateSetting('allow_late_clock_out', parseInt(e.target.value))}
                disabled={!canEdit}
                min="0"
                max="60"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editedSettings.round_clock_times || false}
                onChange={(e) => updateSetting('round_clock_times', e.target.checked)}
                disabled={!canEdit}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Round clock times</span>
            </label>
            
            {editedSettings.round_clock_times && (
              <div className="ml-6">
                <Label htmlFor="round_interval">Rounding Interval (minutes)</Label>
                <Select
                  id="round_interval"
                  value={editedSettings.round_interval?.toString() || '15'}
                  onChange={(e) => updateSetting('round_interval', parseInt(e.target.value))}
                  disabled={!canEdit}
                >
                  <option value="5">5 minutes</option>
                  <option value="6">6 minutes (1/10 hour)</option>
                  <option value="10">10 minutes</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Verification Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Verification Requirements</CardTitle>
          <CardDescription>Configure employee verification methods</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={editedSettings.require_photo_verification || false}
              onChange={(e) => updateSetting('require_photo_verification', e.target.checked)}
              disabled={!canEdit}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium">Require photo verification</span>
          </label>
          
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={editedSettings.require_location_verification || false}
              onChange={(e) => updateSetting('require_location_verification', e.target.checked)}
              disabled={!canEdit}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium">Require location verification</span>
          </label>
        </CardContent>
      </Card>

      {!canEdit && (
        <p className="text-sm text-gray-600 text-center">
          You don't have permission to edit these settings. Contact an administrator if you need to make changes.
        </p>
      )}
    </div>
  )
}